import { Link } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, Mail } from 'lucide-react'

const sections = [
  {
    title: 'Introduction',
    paragraphs: [
      'Welcome to HomiCareplus technologies Limited ("HomiCareplus", "Company", "we", "our", "us").',
      'HomiCareplus is a Nigerian company registered under the laws of the Federal Republic of Nigeria with its registered office in 28 Joseph Avenue, Sangotedo, Lagos - Nigeria.',
      'HomiCareplus provides a platform that connects patients with licensed healthcare professionals for in-home healthcare services.',
      'These Terms of Service ("Terms", "Terms of Service") govern your use of our web pages located at www.homicareplus.com, the Web App: app.homicareplus.com and the HomiCareplus Mobile Application (hereinafter referred to as “Service”).',
      'Our Privacy Policy also governs your use of our Service and explains how we collect, safeguard and disclose information that results from your use of our web pages. Please read it here https://www.homicareplus.com/privacy.',
      'Your agreement with us includes these Terms and our Privacy Policy ("Agreements"). You acknowledge that you have read and understood the Agreements, and agree to be bound by them.',
      'If you do not agree with (or cannot comply with) the agreements, then you may not use the Service. These Terms apply to all visitors, users and others who wish to access or use our Service.',
      'By using the platform, you agree to a binding set of rules intended to support a safe, secure, and professional experience for patients, providers, and visitors.',
    ],
  },
  {
    title: 'Acceptance of Terms',
    bullets: [
      'Using HomiCareplus means you agree to these terms.',
      'If you do not agree, you should not use the platform.',
      'These terms apply to patients, healthcare professionals, and general visitors.',
      'We may update the terms over time, and continued use means you accept those updates.',
      'Users must be at least 18 years old or otherwise use the service with appropriate parental or guardian consent.',
    ],
  },
  {
    title: 'Service Description',
    bullets: [
      'HomiCareplus connects patients with licensed healthcare professionals for in-home healthcare services.',
      'The platform supports booking, payments, and communication between patients and providers.',
      'Clinical services are delivered by independent licensed professionals, not directly by HomiCareplus.',
      'The platform is not a direct source of medical diagnosis, treatment, or professional medical advice.',
      'Services may include consultations, skilled nursing, physical therapy, medication support, and chronic care management.',
    ],
  },
  {
    title: 'User Responsibilities',
    bullets: [
      'Provide accurate, complete, and current information when registering or booking services.',
      'Keep account credentials secure and report unauthorized access promptly.',
      'Use the platform only for lawful purposes and appropriate healthcare-related interactions.',
      'Treat healthcare professionals respectfully during all communications and appointments.',
      'Pay all applicable charges in a timely manner.',
      'Follow care instructions and medical advice provided by healthcare professionals where appropriate.',
    ],
  },
  {
    title: 'Healthcare Professional Requirements',
    bullets: [
      'Providers must hold valid licenses and certifications for their fields of practice.',
      'Providers are expected to maintain any required malpractice or professional coverage.',
      'Credential checks, background screening, and verification processes may be required before access is granted.',
      'Providers must comply with relevant professional standards, ethics, and legal obligations.',
      'Changes to licensing or eligibility status must be reported immediately.',
    ],
  },
  {
    title: 'Limitations and Disclaimers',
    bullets: [
      'HomiCareplus is not itself a healthcare provider and does not practice medicine.',
      'Specific provider availability, appointment slots, and service outcomes cannot be guaranteed.',
      'Emergency cases should always be directed to local emergency services and not handled through the platform.',
      'HomiCareplus is not responsible for the independent clinical judgment or care quality of providers using the platform.',
      'Technology interruptions, network problems, or other outages may affect access temporarily.',
    ],
  },
  {
    title: 'Cancellation and Refund Policy',
    bullets: [
      'Appointments may generally be cancelled up to 2 hours before the scheduled time without penalty.',
      'Late cancellations may attract cancellation fees.',
      'No-shows may be charged the full appointment fee unless caused by a genuine emergency.',
      'Eligible refunds for cancelled services are typically processed back to the user wallet.',
      'If a provider cancels because of an emergency or other unexpected issue, the user may receive a full refund or rescheduling option.',
    ],
  },
]

const additionalTerms = [
  {
    title: 'Intellectual Property',
    body: 'Platform content, branding, trademarks, interface elements, and related intellectual property belong to HomiCareplus or its licensors and may not be copied, redistributed, or reused without permission.',
  },
  {
    title: 'Privacy and Data Protection',
    body: 'Use of the platform is also subject to privacy and data handling practices designed to protect personal and health-related information in line with applicable compliance obligations.',
  },
  {
    title: 'Dispute Resolution',
    body: 'Disputes relating to the platform may be handled through mediation and, where necessary, binding arbitration in accordance with applicable Nigerian law.',
  },
  {
    title: 'Limitation of Liability',
    body: 'To the extent allowed by law, HomiCareplus limits liability to the value paid for relevant services and is not liable for indirect, incidental, or consequential losses arising from platform use.',
  },
  {
    title: 'Termination',
    body: 'Accounts may be suspended or terminated for breaches of these terms. Users may also stop using the platform at any time, but prior obligations remain in effect.',
  },
  {
    title: 'Governing Law',
    body: 'These terms are governed by the laws of Nigeria, and applicable proceedings are subject to the appropriate legal forums within Lagos State, Nigeria.',
  },
]

export default function Terms() {
  return (
    <div className="min-h-[100dvh] bg-[#fcfff8]">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={16} /> Back
        </Link>

        <div className="mt-6 rounded border border-border bg-card px-5 py-6 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-8">
          <img src="/HomiCare Logo.png" alt="HomiCareplus logo" className="h-10 w-auto object-contain" />

          <div className="mt-6 max-w-3xl">
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-foreground">Terms of Service</h1>
            <p className="mt-2 text-sm text-muted-foreground">Last updated: June 15, 2026</p>
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {sections.map((section) => (
            <section key={section.title} className="rounded border border-border bg-card px-5 py-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-6">
              <h2 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">{section.title}</h2>

              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mt-4 text-sm sm:text-base leading-7 text-muted-foreground">
                  {paragraph}
                </p>
              ))}

              {section.bullets && (
                <ul className="mt-4 space-y-3 text-sm sm:text-base leading-7 text-muted-foreground">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section className="rounded border border-border bg-card px-5 py-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-6">
            <h2 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">Additional Terms</h2>
            <div className="mt-5 grid gap-4">
              {additionalTerms.map((item) => (
                <div key={item.title} className="rounded border border-border bg-background px-4 py-4">
                  <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-2 text-sm sm:text-base leading-7 text-muted-foreground">{item.body}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded border border-amber-300 bg-amber-50 px-5 py-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-6">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="mt-0.5 text-amber-700" />
              <div>
                <h2 className="text-2xl font-semibold tracking-[-0.03em] text-amber-900">Important Emergency Notice</h2>
                <p className="mt-3 text-sm sm:text-base leading-7 text-amber-900/80">
                  HomiCareplus is not an emergency service. If you are facing a medical emergency or a life-threatening situation, contact your local emergency number immediately instead of relying on the platform.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded border border-border bg-card px-5 py-5 shadow-[0_10px_40px_rgba(15,23,42,0.04)] sm:px-8 sm:py-6">
            <h2 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">Questions About These Terms?</h2>
            <p className="mt-3 text-sm sm:text-base leading-7 text-muted-foreground">
              If you need clarification about any part of these terms, contact HomiCareplus using the email below.
            </p>

            <div className="mt-5 inline-flex items-center gap-2 rounded border border-border bg-background px-4 py-3 text-sm font-medium text-foreground">
              <Mail size={16} className="text-primary" />
              hello@homicareplus.com
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}